/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2018
 */

/*global angular*/

/**
 * nexleaderSurvey
 *
 * angular component: view
 *
 * This is the IPSAT assessment component. It is responsible for loading the questions from the backend survey object.
 *  Then populating a SurveyResult object from that structure then sending that information to the backend.
 */
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, catchError, forkJoin, of } from 'rxjs';
import { EnumsService } from '../../../../services/enum.service';
import { SurveyService } from '../../../../services/survey.service';
import { UserService } from '../../../../services/user.service';
import { SurveyResultService } from '../survey-result/survey-result.service';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { ArchetypeReportService } from '../../../archetype-report/resources/archetype-report.service';
import { CommonModule } from '@angular/common';
import { OrderByPipe } from '../../../../pipes/orderby.pipe';
import { FormsModule } from '@angular/forms';
import { NexleaderSurveyQuestionComponent } from './question/question.component';

@Component({
  selector: 'app-nexleader-survey',
  standalone: true,
  imports: [
    CommonModule,
    OrderByPipe,
    FormsModule,
    NexleaderSurveyQuestionComponent,
  ],
  templateUrl: './survey.component.html',
})
export class NexleaderSurveyComponent implements OnInit {
  routeParams: any;
  // Enums: any;
  survey: any;
  user: any;
  disableSubmit = false;

  /**
   * "Generating your IPSAT 2.0 report" loading-screen flag. Set true
   * after the survey result saves and the autoGenerate call is in
   * flight, so the user sees a friendly progress message instead of
   * being stuck on the now-disabled submit button for 15-30 seconds
   * while AI personalization runs server-side.
   */
  generatingArchetypeReport = false;

  constructor(
    private enumsService: EnumsService,
    private surveyService: SurveyService,
    private userService: UserService,
    private surveyResultService: SurveyResultService,
    private errorHandlerService: ErrorHandlerService,
    private archetypeReportService: ArchetypeReportService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.routeParams = this.route.snapshot.params;
    forkJoin({
      // enums: this.enumsService.getEnums(),
      survey: this.surveyService.getSurvey(this.routeParams['survey_id']),
      user: this.userService.getUser(this.routeParams['user_id']),
    })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return EMPTY;
        })
      )
      .subscribe(({ survey, user }) => {
        // this.Enums = enums;
        this.survey = survey;
        this.survey.questions.forEach((question: any) => {
          question.answer = {};
        });
        this.user = user;
      });
  }

  isFormInvalid() {
    return this.survey?.questions.some((q: any) =>
      this.isObjectEmpty(q.answer)
    );
  }

  isObjectEmpty(object: any) {
    return Object.keys(object).length === 0;
  }

  submit(): void {
    this.disableSubmit = true;

    delete this.survey._id;
    this.survey.user = this.routeParams['user_id'];
    this.survey.prerequisites = this.user.prerequisites;

    this.surveyResultService
      .save(this.survey)
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return EMPTY;
        })
      )
      .subscribe((result) => {
        // Steve 2026-06-11 item 1: new users (eligibleForFreeArchetypeReport=true)
        // get the 2.0 archetype report auto-generated free at submit time.
        // Show a friendly loading screen during the 15-30s AI run, then
        // navigate to the report itself. If the user is not eligible,
        // navigate immediately like before.
        //
        // Steve 2026-09-12 (launch night): this used to land everyone on the
        // survey-results dashboard, which shows the 1.0 donut alongside a 2.0
        // wheel. Buyers expect the 2.0 report and had to find a subtle link at
        // the top to reach it -- "that's confusing because users will be
        // expecting the 2.0 report".
        if (this.user && this.user.eligibleForFreeArchetypeReport) {
          this.generatingArchetypeReport = true;
          this.archetypeReportService
            .autoGenerate(result._id)
            .pipe(
              catchError((err) => {
                // 403/409 (already has one / not eligible) is fine — silently
                // fall through to the normal results page. Other errors get
                // logged but don't block navigation either; the user can
                // always purchase later via the unlock CTA.
                console.warn('[Survey] autoGenerate skipped/failed:', err);
                return of(null);
              })
            )
            .subscribe((report) => {
              this.generatingArchetypeReport = false;
              // Only jump to the report when generation actually succeeded.
              // catchError above yields null on 403/409/5xx, and the archetype
              // route would render an empty shell for a report that isn't there.
              if (report) {
                this.router.navigate([
                  '/users',
                  this.user._id,
                  'surveyResults',
                  result._id,
                  'archetype',
                ]);
              } else {
                this.router.navigate(['/users', this.user._id, 'surveyResults', result._id]);
              }
            });
        } else {
          this.router.navigate(['/users', this.user._id, 'surveyResults', result._id]);
        }
      });
  }
}

// angular.module('nexleader-ipsat').directive(
//   'ng17NexleaderSurveyComponent',
//   downgradeComponent({
//     component: NexleaderSurveyComponent,
//   }) as angular.IDirectiveFactory
// );
